import { Avatar } from '@tempmee/react';
import { colors, shadows } from '@tempmee/tailwind';
import { FC, useState } from 'react';
import { useNavigate } from 'react-router';

interface IProfilePictureHoverProps {
  id: string;
  profileImage?: string;
  isAvailable?: boolean;
  isFavorite?: boolean;
}

const DEFAULT_SIZE = 68;
const HOVER_SIZE = 78;
const BORDER_WIDTH = 3;
const BORDER_OFFSET = BORDER_WIDTH * 2;

const PURPLE_RING_WIDTH = 3;
const PURPLE_RING_GAP = 3;
const PURPLE_RING_TOTAL = (PURPLE_RING_WIDTH + PURPLE_RING_GAP) * 2;

const SEARCH_ICON_SIZE = 26;
const SEARCH_ICON_BORDER = 3;

// The DS Avatar renders at its largest preset (2xl = 80px) and is CSS-scaled down
// into the exact `imageSize` box below. Every rendered size (<= HOVER_SIZE = 78) is
// a downscale of 80px, so photos stay crisp, and the existing hover-grow / favorite-
// ring / overlay sizing math is preserved unchanged.
const AVATAR_INTRINSIC = 80;

const SearchIcon: FC = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="5.5" cy="5.5" r="4.5" stroke={colors.shades['0']} strokeWidth="2" />
    <line
      x1="9"
      y1="9"
      x2="13"
      y2="13"
      stroke={colors.shades['0']}
      strokeWidth="2"
      strokeLinecap="round"
    />
  </svg>
);

export const ProfilePictureHover: FC<IProfilePictureHoverProps> = ({
  id,
  profileImage,
  isAvailable = true,
  isFavorite = false,
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const navigate = useNavigate();

  const visuallyAvailable = isAvailable || isFavorite;
  const showHover = visuallyAvailable && isHovered;
  const currentSize = showHover ? HOVER_SIZE : DEFAULT_SIZE;

  const hasFavoriteRing = isFavorite;
  const hasNeutralBorder = !visuallyAvailable;

  const imageSize = hasFavoriteRing
    ? currentSize - PURPLE_RING_TOTAL
    : hasNeutralBorder
      ? currentSize - BORDER_OFFSET
      : currentSize;

  const handleMouseEnter = () => {
    if (visuallyAvailable) {
      setIsHovered(true);
    }
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
  };

  const handleClick = () => {
    if (visuallyAvailable) {
      navigate(`/dashboard/professional/${id}`);
    }
  };

  // NOTE(future-DS): no DS shadow token matches this resting shadow; kept as a literal.
  const defaultShadow = '0px 3px 10px 1px rgba(0,0,0,0.15), 0px 1px 2px 0px rgba(0,0,0,0.15)';
  // Exact match for the DS elevation-drop-2dp token.
  const hoverShadow = shadows['elevation-drop-2dp'];

  // Single source for the avatar image; the favorite ring (when present) wraps it below.
  // The neutral border and grayscale only apply in the non-favorite states, matching the
  // original branches (a favorite is always visuallyAvailable, so it never had them).
  const profilePicture = (
    <div
      data-testid="profile-image"
      style={{
        width: imageSize,
        height: imageSize,
        borderRadius: '50%',
        overflow: 'hidden',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        boxShadow: showHover ? hoverShadow : defaultShadow,
        transition: 'width 0.2s, height 0.2s',
        ...(!hasFavoriteRing &&
          hasNeutralBorder && {
            border: `${BORDER_WIDTH}px solid ${colors.neutral['300']}`,
          }),
        ...(!hasFavoriteRing &&
          !isAvailable && {
            filter: 'grayscale(100%)',
            opacity: 0.5,
          }),
      }}
    >
      {/* DS Avatar handles the circle + the missing/broken-image tooth fallback (GOTU-4428). */}
      <Avatar
        src={profileImage || undefined}
        size="2xl"
        alt="Profile"
        style={{
          flexShrink: 0,
          transform: `scale(${imageSize / AVATAR_INTRINSIC})`,
          transition: 'transform 0.2s',
        }}
      />
    </div>
  );

  // NOTE(DS migration): the profile photo is the DS Avatar (scaled into `imageSize`); the
  // surrounding chrome (hover-grow sizing, favorite ring, overlay, search badge) stays as
  // raw, dynamically-styled divs — none of it maps to DS layout primitives.
  return (
    <div
      data-testid="profile-picture-hover"
      onClick={handleClick}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      style={{
        position: 'relative',
        width: HOVER_SIZE,
        height: HOVER_SIZE,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: visuallyAvailable ? 'pointer' : 'default',
        flexShrink: 0,
      }}
    >
      {hasFavoriteRing ? (
        <div
          data-testid="favorite-ring"
          style={{
            width: currentSize,
            height: currentSize,
            borderRadius: '50%',
            border: `${PURPLE_RING_WIDTH}px solid ${colors.favorites['400']}`,
            padding: PURPLE_RING_GAP,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxSizing: 'border-box',
            transition: 'width 0.2s, height 0.2s',
          }}
        >
          {profilePicture}
        </div>
      ) : (
        profilePicture
      )}

      {showHover && (
        <div
          data-testid="hover-overlay"
          style={{
            position: 'absolute',
            width: imageSize,
            height: imageSize,
            borderRadius: '50%',
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            pointerEvents: 'none',
          }}
        >
          <span
            style={{
              color: colors.shades['0'],
              fontSize: 15,
              fontFamily: 'Nunito',
              lineHeight: 1,
              textAlign: 'center',
            }}
          >
            View
          </span>
          <span
            style={{
              color: colors.shades['0'],
              fontSize: 15,
              fontFamily: 'Nunito',
              lineHeight: 1,
              textAlign: 'center',
            }}
          >
            Profile
          </span>
        </div>
      )}

      {visuallyAvailable && (
        <div
          data-testid="search-icon-button"
          style={{
            position: 'absolute',
            bottom: -2,
            right: -2,
            width: SEARCH_ICON_SIZE,
            height: SEARCH_ICON_SIZE,
            borderRadius: '50%',
            backgroundColor: showHover ? colors.secondary['350'] : colors.secondary['500'],
            border: `${SEARCH_ICON_BORDER}px solid ${colors.neutral['50']}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'background-color 0.2s',
          }}
        >
          <SearchIcon />
        </div>
      )}
    </div>
  );
};
